import React from "react";
import { FormattedNumber } from "react-intl";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { SelectField } from "@syos-music/form";
import { Product } from "@syos-music/products";

import { MenuItem, CircularProgress, Typography, Grid } from "@mui/material";

const DEFAULT_FRAGMENT = gql`
  fragment ProductSelectField_defaultFragment on Product {
    id
  }
`;

const makeQuery = (fragment = DEFAULT_FRAGMENT) => {
  const fragmentName = fragment?.definitions.find(
    (def) =>
      def.kind === "FragmentDefinition" &&
      def.typeCondition.name.value === "Product"
  )?.name.value;

  if (!fragmentName) throw new Error("Cannot extract fragment name");

  return gql`
    query($isB2B: Boolean, $disabled: Boolean) {
      products(isB2B: $isB2B, disabled: $disabled) {
        id
        name
        displayMode

        ...Product_product
        ...${fragmentName}
      }
    }

    ${Product.fragments.product}
    ${fragment}
  `;
};

export default function ProductSelectField({
  priceTaxRate,
  priceCurrency,
  isB2B,
  fragment,
  SelectProps,
  ...props
}) {
  const query = React.useMemo(() => makeQuery(fragment), [fragment]);
  const { data, loading } = useQuery(query, {
    variables: {
      isB2B,
      disabled: false,
    },
  });

  const groups = React.useMemo(() => {
    if (!data?.products) return [];

    const getProductGroup = (product) => {
      switch (product.displayMode) {
        case "originalsMouthpiece":
          return { weight: 1, name: "Bec originals" };

        case "signatureMouthpiece":
          return { weight: 2, name: "Bec Signature" };

        case "customMouthpiece":
          return { weight: 3, name: "Bec sur-mesure" };

        case "mouthpieceLigature":
        case "mouthpieceCap":
        case "accessoriesPack":
        case "cushionsPack":
          return { weight: 4, name: "Accessoires" };

        default:
          return { weight: 5, name: "Autre" };
      }
    };

    const result = [...data?.products]
      .sort((a, b) => {
        const grpA = getProductGroup(a);
        const grpB = getProductGroup(b);

        if (grpA.weight < grpB.weight) return -1;
        if (grpA.weight > grpB.weight) return 1;

        return a.name.localeCompare(b.name);
      })
      .reduce((acc, product) => {
        const group = getProductGroup(product);

        acc[group.name] = acc[group.name] ?? [];
        acc[group.name].push(product);

        return acc;
      }, {});

    return Object.entries(result);
  }, [data?.products]);

  return (
    <SelectField
      SelectProps={{
        renderValue: (productId) => {
          const product = data?.products?.find(({ id }) => id === productId);
          return <Product.Title product={product} />;
        },
        ...SelectProps,
      }}
      {...props}
      native={false}
      parse={(id) => data?.products?.find((product) => product.id === id)}
      format={(product) => (loading ? null : product?.id ?? null)}
    >
      {loading && (
        <MenuItem disabled>
          <CircularProgress size="1em" sx={{ mr: 1 }} />
          Loading…
        </MenuItem>
      )}

      {groups.map(([groupName, products]) => [
        <MenuItem key={groupName} disabled>
          <Typography color="primary">
            <strong>{groupName}</strong>
          </Typography>
        </MenuItem>,

        ...products.map((product) => (
          <MenuItem key={product.id} value={product.id}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <Typography>
                  <Product.Title product={product} />
                </Typography>
                <Typography color="textSecondary">
                  <small>{product.name}</small>
                </Typography>
              </Grid>

              <Grid item>
                <Typography align="right" color="primary">
                  <strong>
                    <Product.Price
                      product={product}
                      taxRate={priceTaxRate}
                      currency={priceCurrency}
                    />
                  </strong>
                </Typography>
                <Typography align="right" color="textSecondary">
                  <small>
                    tax : 
                    <FormattedNumber
                      value={priceTaxRate}
                      // eslint-disable-next-line react/style-prop-object
                      style="percent"
                      minimumFractionDigits={1}
                      maximumFractionDigits={1}
                    />
                  </small>
                </Typography>
              </Grid>
            </Grid>
          </MenuItem>
        )),
      ])}
    </SelectField>
  );
}

ProductSelectField.defaultProps = {
  priceTaxRate: 0,
};
